import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { ButtonGroupProps, ButtonGroupVariant, GroupRole } from './button-group-types';
import styles from './button-group.module.scss';

export const ButtonGroup = ({
  children,
  activeClassOverride,
  defaultClickedIndex,
  selectedIndex,
  onIndexChange,
  addClass = '',
  groupRole = 'listbox',
  ariaLabel,
  variant = ButtonGroupVariant.Primary,
}: ButtonGroupProps): React.ReactElement | null => {
  const [clicked, setClicked] = useState<number | undefined>(defaultClickedIndex);
  const activeIndex = selectedIndex ?? clicked;

  const activeClass = activeClassOverride || styles.active;
  const wrapperClasses = classNames(styles['button-group-wrapper'], styles[variant as ButtonGroupVariant], addClass);
  const buttonRoles: Record<GroupRole, 'option' | 'tab'> = {
    listbox: 'option',
    tablist: 'tab',
  };

  useEffect(() => {
    if (selectedIndex === undefined && defaultClickedIndex !== undefined) {
      setClicked(defaultClickedIndex);
    }
  }, [defaultClickedIndex, selectedIndex]);

  return (
    <div className={wrapperClasses} role={groupRole} aria-label={ariaLabel}>
      {React.Children.map(children, (child, index) =>
        React.cloneElement(child, {
          onClick: (e: React.MouseEvent<HTMLButtonElement>) => {
            if (selectedIndex === undefined) {
              setClicked(index);
            }
            if (child.props.onClick) {
              child.props.onClick(e);
            }
            if (onIndexChange) {
              onIndexChange(index);
            }
          },
          className: `${child.props.className} ${
            activeIndex === index ? classNames(styles.button, activeClass) : styles.button
          }`,
          role: buttonRoles[groupRole || 'listbox'],
          'aria-selected': activeIndex === index,
        }),
      )}
    </div>
  );
};
